import { useEffect, useMemo, useState } from "react";
import { Modal } from "../../ui/modal";
import { CurrencyCode, Wallet } from "../types";
import { createWallet, requestBvnOTP } from "../API/services";
import { useCurrencyStore } from "../../../stores/useCurrencyStore";
import { ChevronDownIcon } from "../../../icons";
import Spinner from "../../ui/Spinner/Spinner";
import { XCircle } from "lucide-react";
import Alert from "../../ui/alert/Alert";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  existingWallets: Wallet[];
  preSelectedCurrencyCode?: string;
}

const CreateWalletModal = ({
  isOpen,
  onClose,
  onCreated,
  existingWallets,
  preSelectedCurrencyCode,
}: Props) => {
  const {  getWalletSupportedCurrencies, walletSupportedCurrencies, hasWalletSupportedLoaded, loadingCurrency } =
    useCurrencyStore();

  const [selectedCurrency, setSelectedCurrency] = useState<
    (typeof walletSupportedCurrencies)[number] | null
  >(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const [bvn, setBvn] = useState("");
  const [companyRegNumber, setCompanyRegNumber] = useState("");
  const [requestingOtp, setRequestingOtp] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [verified, setVerified] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  /* ---------------- Load currencies once ---------------- */
  useEffect(() => {
    if (isOpen && !hasWalletSupportedLoaded) {
      getWalletSupportedCurrencies();
    }
  }, [isOpen, hasWalletSupportedLoaded, getWalletSupportedCurrencies]);

    useEffect(() => {
    if (isOpen && preSelectedCurrencyCode && walletSupportedCurrencies.length > 0) {
      const preSelectedCurrency = walletSupportedCurrencies.find(
        (c) => c.code === preSelectedCurrencyCode && c.allowWalletCreation
      );
      if (preSelectedCurrency) {
        setSelectedCurrency(preSelectedCurrency);
      }
    }
  }, [isOpen, preSelectedCurrencyCode, walletSupportedCurrencies]);

  /* ---------------- Existing wallet codes ---------------- */
  const existingCodes = useMemo<CurrencyCode[]>(
    () => existingWallets.map((w) => w.currency),
    [existingWallets]
  );

  /* ---------------- Available currencies ---------------- */
  const availableCurrencies = useMemo(
    () =>
      walletSupportedCurrencies.filter(
        (c) =>
          c.allowWalletCreation &&
          !existingCodes.includes(c.code as CurrencyCode)
      ),
    [walletSupportedCurrencies, existingCodes]
  );

  const isNGN = selectedCurrency?.code === "NGN";

  /* ---------------- Request BVN OTP ---------------- */
  const handleRequestOtp = async () => {
    if (!bvn || requestingOtp) return;

    if (bvn.length !== 11) {
      setError("BVN must be exactly 11 digits");
      setTimeout(() => setError(null), 4000);
      return;
    }

    try {
      setRequestingOtp(true);
      setError(null);
      setSuccessMessage(null);

      await requestBvnOTP(bvn);

      setOtpRequested(true);
      setSuccessMessage("BVN verification successful.");
      setVerified(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to request OTP");
      setTimeout(() => {
        setError(null);
      }, 2000);
    } finally {
      setRequestingOtp(false);
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    }
  };

  /* ---------------- Create Wallet ---------------- */
  const handleCreate = async () => {
    if (!selectedCurrency || submitting) return;

    if (isNGN && (!bvn || !companyRegNumber)) {
      setError("BVN and Company Registration Number are required for NGN wallets");
      setTimeout(() => setError(null), 4000);
      return;
    }

    if (isNGN && bvn.length !== 11) {
      setError("BVN must be exactly 11 digits");
      setTimeout(() => setError(null), 4000);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);

      await createWallet(
        isNGN
          ? { currency: selectedCurrency.code, bvn, companyRegistrationNumber: companyRegNumber }
          : { currency: selectedCurrency.code }
      );

       setSuccessMessage("Wallet created successfully.");
    
    // Wait a moment for user to see success, then close and refresh
    setTimeout(() => {
      onClose();
      onCreated();
    }, 800); 
    } catch (err: any) {
      setError(err?.response?.data?.message || "Unable to create wallet");
      setTimeout(() => {
        setError(null);
      }, 2000);
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------- Reset on close ---------------- */
  useEffect(() => {
    if (!isOpen) {
      setSelectedCurrency(null);
      setShowDropdown(false);
      setBvn("");
      setCompanyRegNumber("");
      setOtpRequested(false);
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      className="max-w-md rounded-2xl border border-gray-200 bg-white p-6
        dark:border-gray-800 dark:bg-gray-900"
    >
      {/* Header */}
      <h2 className="mb-1 text-lg font-semibold text-gray-800 dark:text-white/90">
        Create Wallet
      </h2>

      <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
        Select a currency to create a new wallet
      </p>

      {/* Alerts */}
      {successMessage && (
        <div className="mb-4">
          <Alert variant="success" title="Success" message={successMessage} />
        </div>
      )}

      {error && (
        <div className="mb-4">
          <Alert variant="error" title="Error" message={error} />
        </div>
      )}

      {/* Loading */}
      {loadingCurrency ? (
        <div className="flex h-64 items-center justify-center">
          <Spinner className="h-10 w-10 text-primary" />
        </div>
      ) : availableCurrencies.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <XCircle className="mb-4 h-10 w-10 text-red-500" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            You already have wallets for all supported currencies.
          </p>
        </div>
      ) : (
        <>
          {/* Currency Dropdown */}
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Currency
          </label>

          <div className="relative">
            <button
              type="button"
              onClick={() => setShowDropdown((p) => !p)}
              className="flex w-full items-center rounded-lg border border-gray-200
                bg-gray-50 px-4 py-3 text-gray-800
                hover:bg-gray-100 focus:outline-none
                dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
            >
              {selectedCurrency ? (
                <>
                  <img
                    src={selectedCurrency.flag}
                    alt={selectedCurrency.code}
                    className="mr-2 h-5 w-5 rounded-full"
                  />
                  <span>{selectedCurrency.name}</span>
                </>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">
                  Choose currency
                </span>
              )}
              <ChevronDownIcon className="ml-auto h-5 w-5 text-gray-400" />
            </button>

            {showDropdown && (
              <div
                className="absolute z-20 mt-1 max-h-60 w-full overflow-auto
                rounded-md border border-gray-200 bg-white shadow-lg
                dark:border-gray-700 dark:bg-gray-800"
              >
                {availableCurrencies.map((currency) => (
                  <div
                    key={`${currency.code}-${currency.iso2}`}
                    onClick={() => {
                      setSelectedCurrency(currency);
                      setShowDropdown(false);
                    }}
                    className="flex cursor-pointer items-center px-4 py-2
                      hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <img
                      src={currency.flag}
                      alt={currency.code}
                      className="mr-2 h-5 w-5 rounded-full"
                    />
                    <span className="text-gray-700 dark:text-white">
                      {currency.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* NGN Fields */}
          {isNGN && (
            <div className="mt-5 space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  BVN
                </label>
                <div className="flex gap-2">
                  <input
                    value={bvn}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ""); // digits only
                      if (value.length <= 11) {
                        setBvn(value);
                      }
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={11}
                    placeholder="Enter BVN of one of the directors/founders"
                    className="w-full rounded-lg border border-gray-200 bg-gray-50
    px-4 py-3 text-sm text-gray-900
    focus:outline-none focus:ring-2 focus:ring-primary/30
    dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />

                  <button
                    onClick={handleRequestOtp}
                    disabled={!bvn || bvn.length !== 11 || requestingOtp || verified}
                    className="rounded-lg border border-gray-200 bg-gray-50
                      px-4 py-3 text-sm text-gray-700 hover:bg-gray-100
                      disabled:opacity-50
                      dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
                  >
                    {requestingOtp ? "Sending..." : "Verify BVN"}
                  </button>
                </div>
              </div>

              {otpRequested && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Company Registration Number
                  </label>
                  <input
                    value={companyRegNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ""); // digits onl

                      setCompanyRegNumber(value);
                    }}
                    placeholder="Enter Company Registration Number (RC Number)"
                    className="w-full rounded-lg border border-gray-200 bg-gray-50
                      px-4 py-3 text-sm text-gray-900
                      focus:outline-none focus:ring-2 focus:ring-primary/30
                      dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg border border-gray-300 bg-white px-4 py-3
                text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50
                dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
            >
              Cancel
            </button>

            <button
              onClick={handleCreate}
              disabled={
                submitting || !selectedCurrency || (isNGN && (!bvn || !companyRegNumber))
              }
              className="rounded-lg border border-gray-300 bg-white px-4 py-3
                text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50
                dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
            >
              {submitting ? "Creating..." : "Create Wallet"}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default CreateWalletModal;
